import type {
  FrictionSignal,
  NarrationDelta,
  OverlayCommand,
  PicoSdk,
  SdkMode,
} from '@pique-so/pico-sdk';

const APP_ID = 'pique-desktop-web';
const DEFAULT_ENDPOINT = 'wss://pico.pique.so/ws';
const STORAGE_KEY_ENDPOINT = 'pico-endpoint';
const STORAGE_KEY_MODE = 'pico-mode';
const STORAGE_KEY_ENABLED = 'pique-enable-pico-sdk';
const SESSION_COOKIE = 'pique_session';

function isPicoSdkEnabled(): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_ENABLED);
    if (raw === null) return false;
    return JSON.parse(raw) === true;
  } catch {
    return false;
  }
}

function getStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parseMode(value: string | null): SdkMode {
  if (value === 'passive' || value === 'capture' || value === 'live') {
    return value;
  }
  return 'live';
}

function readSessionCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const pattern = new RegExp(`(?:^|;\\s*)${SESSION_COOKIE}=([^;]+)`);
  const match = document.cookie.match(pattern);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

async function readTauriVaultToken(): Promise<string | null> {
  if (!environment.isTauri) return null;
  try {
    const mod = await import(
      '@pique/core/modules/cloud/utils/tauri-auth'
    );
    await mod.refreshTauriAuthToken();
    return mod.getCachedTokenSync();
  } catch (err) {
    console.warn('[pico] tauri vault read failed:', err);
    return null;
  }
}

async function resolveAuthToken(): Promise<{
  token: string;
  source: 'cookie' | 'tauri-vault';
} | null> {
  const cookieToken = readSessionCookie();
  if (cookieToken) return { token: cookieToken, source: 'cookie' };

  const vaultToken = await readTauriVaultToken();
  if (vaultToken) return { token: vaultToken, source: 'tauri-vault' };

  return null;
}

interface NarrationPopup {
  push: (n: NarrationDelta) => void;
  destroy: () => void;
}

function createNarrationPopup(): NarrationPopup {
  if (typeof document === 'undefined') {
    return { push: () => undefined, destroy: () => undefined };
  }

  const host = document.createElement('div');
  host.id = 'pico-narration';
  host.setAttribute('aria-live', 'polite');
  host.setAttribute('aria-atomic', 'false');
  Object.assign(host.style, {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    maxWidth: '360px',
    padding: '12px 14px',
    background: 'rgba(15, 18, 32, 0.92)',
    color: '#e6e8ee',
    font: '13px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 12px 36px rgba(0,0,0,0.35)',
    backdropFilter: 'blur(12px)',
    opacity: '0',
    transform: 'translateY(8px)',
    transition: 'opacity 180ms ease, transform 180ms ease',
    zIndex: '2147483647',
    pointerEvents: 'none',
    whiteSpace: 'pre-wrap',
  } as CSSStyleDeclaration);

  const badge = document.createElement('div');
  badge.textContent = 'pico is watching';
  Object.assign(badge.style, {
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#8892a8',
    marginBottom: '6px',
  } as CSSStyleDeclaration);

  const body = document.createElement('div');
  body.id = 'pico-narration-body';
  host.append(badge, body);

  const mount = () => {
    if (host.isConnected) return;
    document.body.append(host);
  };

  let buffer = '';
  let fadeTimer: ReturnType<typeof setTimeout> | null = null;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const show = () => {
    mount();
    host.style.opacity = '1';
    host.style.transform = 'translateY(0)';
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const fade = () => {
    if (fadeTimer) clearTimeout(fadeTimer);
    fadeTimer = setTimeout(() => {
      host.style.opacity = '0';
      host.style.transform = 'translateY(8px)';
      hideTimer = setTimeout(() => {
        buffer = '';
        body.textContent = '';
      }, 220);
    }, 3500);
  };

  return {
    push(n: NarrationDelta) {
      if (n.done && n.text.length === 0) {
        if (buffer.length > 0) fade();
        return;
      }
      if (fadeTimer) {
        clearTimeout(fadeTimer);
        fadeTimer = null;
      }
      buffer += n.text;
      body.textContent = buffer;
      show();
      if (n.done) fade();
    },
    destroy() {
      if (fadeTimer) clearTimeout(fadeTimer);
      if (hideTimer) clearTimeout(hideTimer);
      host.remove();
    },
  };
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `sid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

interface RunningSdk {
  sdk: PicoSdk;
  popup: NarrationPopup;
}

const SDK_STATE: { current: RunningSdk | null; starting: Promise<RunningSdk | null> | null } = {
  current: null,
  starting: null,
};

export const PICO_CONFIG_CHANGED_EVENT = 'pico:config-changed';

export function stopPico(): void {
  const running = SDK_STATE.current;
  if (!running) return;
  SDK_STATE.current = null;
  try {
    running.sdk.stop();
  } catch (err) {
    console.warn('[pico] stop sdk failed:', err);
  }
  running.popup.destroy();
  delete (window as unknown as { __pico?: PicoSdk }).__pico;
  console.info('[pico] stopped — flip the experimental flag back on to resume');
}

async function startPicoOnce(): Promise<RunningSdk | null> {
  if (typeof window === 'undefined') return null;

  if (!isPicoSdkEnabled()) {
    console.info(
      '[pico] disabled via experimental flag — flip the "Enable Pico SDK" switch in Settings > Appearance > Pico and reload to turn on'
    );
    return null;
  }

  const auth = await resolveAuthToken();
  if (!auth) {
    console.info(
      '[pico] no auth token (cookie missing, Tauri vault empty); SDK stays inert'
    );
    return null;
  }

  const endpoint = getStored(STORAGE_KEY_ENDPOINT) ?? DEFAULT_ENDPOINT;
  const mode = parseMode(getStored(STORAGE_KEY_MODE));

  console.info(
    `[pico] bootstrap auth=${auth.source} endpoint=${endpoint} mode=${mode}`
  );

  const { createPicoSdk } = await import('@pique-so/pico-sdk');

  const onFrictionSignal = (signal: FrictionSignal) => {
    console.debug(
      '[pico] friction',
      signal.kind,
      signal.severity ?? 1,
      signal.meta ?? {}
    );
  };

  const onOverlay = (command: OverlayCommand) => {
    console.debug(
      '[pico] overlay',
      command.action,
      `${command.target.role}:${command.target.name}`,
      command.surface
    );
  };

  const popup = createNarrationPopup();
  const onNarration = (n: NarrationDelta) => popup.push(n);

  const sdk = createPicoSdk({
    endpoint,
    sessionId: newSessionId(),
    appId: APP_ID,
    mode,
    sampling: 1,
    lazyInit: true,
    authToken: auth.token,
    onFrictionSignal,
    onOverlay,
    onNarration,
  });

  sdk.start();
  (window as unknown as { __pico?: PicoSdk }).__pico = sdk;
  console.info('[pico] started — inspect via window.__pico');
  return { sdk, popup };
}

export async function bootstrapPico(): Promise<PicoSdk | null> {
  if (typeof window === 'undefined') return null;

  if (SDK_STATE.current) return SDK_STATE.current.sdk;
  if (SDK_STATE.starting) {
    const result = await SDK_STATE.starting;
    return result?.sdk ?? null;
  }

  SDK_STATE.starting = startPicoOnce();
  try {
    const result = await SDK_STATE.starting;
    if (result) SDK_STATE.current = result;
    return result?.sdk ?? null;
  } finally {
    SDK_STATE.starting = null;
  }
}

let LISTENERS_INSTALLED = false;

function installConfigListeners(): void {
  if (LISTENERS_INSTALLED || typeof window === 'undefined') return;
  LISTENERS_INSTALLED = true;

  const sync = () => {
    if (isPicoSdkEnabled()) {
      if (!SDK_STATE.current && !SDK_STATE.starting) {
        void bootstrapPico().catch(err =>
          console.warn('[pico] reactive bootstrap failed:', err)
        );
      }
    } else if (SDK_STATE.current) {
      stopPico();
    }
  };

  window.addEventListener('storage', e => {
    if (e.key === STORAGE_KEY_ENABLED) sync();
  });
  window.addEventListener(PICO_CONFIG_CHANGED_EVENT, sync);
}

installConfigListeners();
